import { allChains, getCurrencyKeys } from "../utils/currencies";

export const projectTipsData = {
  summary: (
    <p className="py-5 px-3 text-justify leading-8 text-darkblue">
      Here's a short instruction with examples of how to fill the Project
      Summary:
      <br />
      <br />
      <b>Problem Statement</b>: Clearly state the problem or issue that your
      project aims to address. This should be a concise statement that
      highlights the significance of the problem.
      <br />
      <span className="underline">Example:</span> The lack of access to clean
      drinking water is a major problem in many rural communities in developing
      countries, leading to a range of health issues and a reduced quality of
      life.
      <br />
      <b>Objectives:</b> Outline the specific objectives of your project. These
      should be clear, measurable, and achievable goals that will help to
      address the problem statement.
      <br />
      <span className="underline">Example:</span>
      The objectives of this project are to design and implement a low-cost
      water filtration system that can be easily maintained by community
      members, to train local residents in the operation and maintenance of the
      system, and to monitor the impact of the system on water quality and
      health outcomes.
      <br />
      <b>Methodology:</b> Briefly describe the methods and approaches that you
      will use to achieve your objectives. This should include a summary of the
      research, design, and implementation processes that you will undertake.
      <br />
      <span className="underline">Example:</span>
      The project will involve a combination of laboratory testing, field
      research, and community engagement activities. We will work with local
      partners to identify the most appropriate filtration technology, and will
      conduct extensive testing to ensure that the system is effective in
      removing contaminants. We will also provide training to community members
      on the operation and maintenance of the system, and will monitor water
      quality and health outcomes over time.
      <br />
      <b>Expected outcomes:</b> Describe the expected outcomes of your project.
      This should include both short-term and long-term impacts, and should be
      linked to your objectives.
      <br />
      <span className="underline">Example:</span> The expected outcomes of this
      project include improved access to clean drinking water for community
      members, a reduction in water-related health issues, and increased
      community capacity to maintain and operate the filtration system over the
      long term.
      <br />
      <b>Conclusion:</b> Summarize the key points of your project summary, and
      emphasize the importance of your work in addressing the identified
      problem.
      <br />
      <span className="underline">Example:</span> This project aims to address
      the critical issue of access to clean drinking water in rural communities.
      By designing and implementing a low-cost filtration system and providing
      training to community members, we hope to make a significant impact on
      water quality and health outcomes.
    </p>
  ),
  beneficiaries: (
    <p className="py-5 px-3 text-justify leading-8 text-darkblue">
      Here's a short instruction with examples of how to fill the Project
      Beneficiaries:
      <br />
      <b>Identify the target beneficiaries:</b> Clearly identify the group or
      groups of people who will benefit from your project. This should be a
      specific and well-defined group, and should be linked to the problem
      statement and objectives of your project.
      <br />
      <span className="underline">Example:</span> The target beneficiaries of
      this project are rural communities in developing countries who lack access
      to clean drinking water.
      <br />
      <b>Outline the benefits of the project:</b> Describe the specific benefits
      that the target beneficiaries will receive from your project. This should
      be linked to the expected outcomes of your project.
      <br />
      <span className="underline">Example:</span> The target beneficiaries of
      this project will benefit from improved access to clean drinking water,
      which will lead to a reduction in water-related health issues and an
      improved quality of life. They will also gain knowledge and skills in the
      operation and maintenance of the filtration system, which will increase
      their capacity to maintain and operate the system over the long term.
      <br />
      <b>Describe the impact of the project:</b> Describe the potential impact
      of your project on the target beneficiaries. This should be linked to the
      expected outcomes of your project.
      <br />
      <span className="underline">Example:</span> The impact of this project on
      the target beneficiaries will be significant, as it will improve their
      access to clean drinking water and reduce the incidence of water-related
      health issues. It will also increase their capacity to maintain and
      operate the filtration system over the long term, which will have a
      lasting impact on their quality of life.
      <br />
      <b>Conclusion:</b> Summarize the key points of your project beneficiaries,
      and emphasize the importance of your work in addressing the needs of the
      target beneficiaries.
    </p>
  ),
  equipment: (
    <p className="py-5 px-3 text-justify leading-8 text-darkblue">
      Here's a short instruction with examples of how to fill the Tools,
      Equipment, and Materials:
      <b>Identify the tools, equipment, and materials needed:</b> Make a list of
      all the tools, equipment, and materials that will be required to complete
      the project. This should be based on the project plan and the specific
      tasks that need to be completed.
      <br />
      <span className="underline">Example:</span> The tools, equipment, and
      materials needed for this project include water filtration systems, water
      testing kits, piping, valves, pumps, and other plumbing equipment.
      <br />
      <b>Estimate the quantity of each item:</b> Estimate the quantity of each
      tool, equipment, and material that will be needed to complete the project.
      This should be based on the project plan and the specific tasks that need
      to be completed.
      <br />
      <span className="underline">Example:</span> We will need to purchase 10
      water filtration systems, 20 water testing kits, 100 meters of piping, 50
      valves, 10 pumps, and other plumbing equipment.
      <br />
      <b>Conclusion:</b> Summarize the key points of your tools, equipment, and
      materials list, and emphasize the importance of having the necessary
      resources to complete the project successfully.
    </p>
  ),
  funding_goal: (
    <p className="py-5 px-3 text-justify leading-8 text-darkblue">
      Include the expected target amount for your NFT fundraising event based on
      the proposed budget details. Provide detailed information to help
      stakeholders understand the purpose and scope of your fundraising efforts.
    </p>
  ),

  minimum_offer: (
    <p className="py-5 px-3 text-justify leading-8 text-darkblue">
      Set the minimum amount a contributor must provide in a single contribution (in US dollars). This helps ensure meaningful funding while making your project accessible to supporters.
    </p>
  ),

  crowdfunding_expiration: (
    <p className="py-5 px-3 text-justify leading-8 text-darkblue">
      Please determine the closing date of your crowdfunding event.
    </p>
  ),
};

export const disabledButtonData = {
  "Closed Call": "Funding deadline has been reached",
  "Successfully Funded": "This project has been Successfully Funded",
  Draft: "This project is in draft mode",
};

export const MAX_RANGE_PRICE = {
  ALL: 100,
  WETH: 10,
  POL: 10000,
  USDT: 10000,
  USDC: 10000,
  DAI: 5000,
};

export const TIMELINE_DESCRIPTION =
  "As a researcher, my commitment to fulfilling the task within the proposed schedule would involve dedicating the necessary time and resources to each phase of the project, staying organized and focused, and communicating effectively with any team members or stakeholders involved in the project.";
export const BUDGET_DESCRIPTION =
  "As a researcher, my commitment to handling the expenditure for the project would involve adhering to the budget outlined in the table and ensuring that all expenses are necessary and justified.";

export const projectTabs = [
  {
    id: 1,
    title: "Overview",
    url: "details",
    shouldDisableOnPreview: false,
  },
  {
    id: 2,
    title: "Roadmap & Budget",
    url: "timetable-budget",
    shouldDisableOnPreview: false,
  },
  {
    id: 3,
    title: "Contributors",
    url: "contributors",
    shouldDisableOnPreview: true,
  },
  {
    id: 4,
    title: "Results & Outcomes",
    url: "results",
    shouldDisableOnPreview: true,
  },
  {
    id: 5,
    title: "Community",
    url: "discussion",
    shouldDisableOnPreview: true,
  },
];

export const currencyTokenDropDown = () => {
  const currencies = getCurrencyKeys().map((item) => ({
    value: item,
    title: item,
  }));
  const other = [
    {
      value: "all",
      title: "All",
    },
    {
      value: "Cross-chain",
      title: "Cross-chain",
    },
  ];

  return [...other, ...currencies];
};

export const blockchainsDropDown = () => {
  const chains = allChains.map((chain) => ({
    value: chain.ethersName,
    title: chain.name,
  }));
  const other = [
    {
      value: "all",
      title: "All",
    },
    {
      value: "Cross-chain",
      title: "Cross-chain",
    },
  ];
  return [...other, ...chains];
};
